"use client";

import { useRef, useState } from "react";
import DrawingPage from "./_components/DrawingPage";
import AudioPracticePage from "./_components/AudioPracticePage";
import AudioFeedback from "./_components/AudioFeedback";
import DrawingFeedback from "./_components/DrawingFeedback";
import type { DrawingBoardHandle } from "./_components/DrawingBoard";
import type { PracticeRecordingHandle } from "./_components/PracticeRecording";
import { useProfileContext } from "../../context/ProfileContext";
import { checkStudentAnswer } from "../../services/backend/api";

// Main App Component
export default function KidsDrawingApp() {
  const [currentPage, setCurrentPage] = useState("audio"); // "audio" or "drawing"
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");

  const [audioSubmitted, setAudioSubmitted] = useState(false);
  const [audioRecordingPassed, setAudioRecordingPassed] = useState(false);
  const [showAudioFeedback, setShowAudioFeedback] = useState(false);

  const [drawingPassed, setDrawingPassed] = useState(false);
  const [showDrawingFeedback, setShowDrawingFeedback] = useState(false);

  const drawingRef = useRef<DrawingBoardHandle | null>(null);
  const recordingRef = useRef<PracticeRecordingHandle | null>(null);

  const { currentWord, selectedProfile, getOrUpdateToNextWord } =
    useProfileContext();

  const skipWord = () => {
    resetSession();
    getOrUpdateToNextWord();
  };

  const resetSession = () => {
    setShowAudioFeedback(false);
    setShowDrawingFeedback(false);
    setCurrentPage("audio");
    setUploadStatus("");
    setAudioSubmitted(false);
  };

  const handleSubmitEnglishAudio = async () => {
    setIsUploading(true);
    const audioBlob = await recordingRef.current?.getEnglishAudioBlob();
    if (!audioBlob || !currentWord) {
      setUploadStatus(
        "❌ No English audio recorded. Please record before submitting."
      );
      setIsUploading(false);
      return;
    }

    try {
      const hasPassed = await checkStudentAnswer({
        correct: null,
        studentId: selectedProfile?.studentId,
        difficulty: currentWord!.difficulty,
        file: audioBlob,
        type: "audio_english",
        wordEnglish: currentWord!.english,
        skip: false,
      });

      setAudioRecordingPassed(hasPassed);
      setShowAudioFeedback(true);
    } catch (error) {
      console.error("Error checking student answer:", error);
      setUploadStatus("❌ Oops! Could not submit audio. Try again later.");
    } finally {
      setIsUploading(false);
    }
  };

  // const handleSubmitTeluguAudio = async () => {
  //   const audioBlob = await recordingRef.current?.getTeluguAudioBlob();
  //   if (!audioBlob || !currentWord) {
  //     setUploadStatus(
  //       "❌ No Telugu audio recorded. Please record before submitting."
  //     );
  //     return;
  //   }

  //   try {
  //     const hasPassed = await checkStudentAnswer({
  //       correct: null,
  //       studentId: selectedProfile?.studentId,
  //       difficulty: currentWord!.difficulty,
  //       file: audioBlob,
  //       type: "audio_telugu",
  //       wordEnglish: currentWord!.english,
  //       skip: false,
  //     });

  //     setAudioRecordingPassed(hasPassed);
  //     setShowAudioFeedback(true);
  //     setAudioSubmitted(true);
  //     setCurrentPage("drawing");
  //   } catch (error) {
  //     console.error("Error checking student answer:", error);
  //     setUploadStatus("❌ Oops! Could not submit audio. Try again later.");
  //   }
  // };

  const handleSubmitDrawing = async () => {
    setIsUploading(true);
    try {
      const imageBlob = await drawingRef.current?.getImageBlob();

      if (!imageBlob) {
        setUploadStatus("❌ No drawing made. Please draw before sharing.");
        return;
      }

      const hasPassed = await checkStudentAnswer({
        correct: null,
        studentId: selectedProfile?.studentId,
        difficulty: currentWord!.difficulty,
        file: imageBlob,
        type: "image",
        wordEnglish: currentWord!.english,
        skip: false,
      });
      setDrawingPassed(hasPassed);
      setShowDrawingFeedback(true);
      setUploadStatus("✅ Drawing submitted successfully!");
    } catch (err) {
      console.error("Drawing submission error:", err);
      setUploadStatus("❌ Oops! Could not submit drawing. Try again later.");
    } finally {
      setIsUploading(false);
    }
  };

  if (showDrawingFeedback) {
    return (
      <DrawingFeedback
        isSuccess={drawingPassed}
        onSkip={skipWord}
        onNextWord={skipWord}
        onDrawAgain={() => {
          setShowDrawingFeedback(false);
        }}
      />
    );
  }

  if (showAudioFeedback) {
    return (
      <AudioFeedback
        isSuccess={audioRecordingPassed}
        onSkip={skipWord}
        tryAgain={() => setShowAudioFeedback(false)}
        goToDrawingPage={() => {
          setCurrentPage("drawing");
          setShowAudioFeedback(false);
        }}
      />
    );
  }

  if (currentPage === "audio") {
    return (
      <AudioPracticePage
        recordingRef={recordingRef}
        onNext={() => {
          setCurrentPage("drawing");
          setUploadStatus("");
        }}
        onSkip={skipWord}
        onSubmitAudio={() => {
          handleSubmitEnglishAudio();
        }}
        isUploading={isUploading}
        uploadStatus={uploadStatus}
        audioSubmitted={audioSubmitted}
      />
    );
  }

  return (
    <DrawingPage
      drawingRef={drawingRef}
      onBack={() => setCurrentPage("audio")}
      onSubmit={handleSubmitDrawing}
      onSkip={skipWord}
      isUploading={isUploading}
      uploadStatus={uploadStatus}
    />
  );
}
